/**
 * AvatarControls Component
 * Controls for avatar interaction - voice input, interrupt, chat toggle
 * Uses browser SpeechRecognition for voice input (bypasses HeyGen's AI)
 * Positioned vertically on right side
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStreamingAvatarContext } from '../context/StreamingAvatarContext';
import { useInterrupt } from '../hooks/useInterrupt';
import './AvatarControls.css';

interface AvatarControlsProps {
  showChatToggle?: boolean;
  // Desktop chat (ChatHistoryPanel)
  isDesktopChatVisible?: boolean;
  onToggleDesktopChat?: () => void;
  // Mobile chat (LiveChatOverlay)
  isMobileChatVisible?: boolean;
  onToggleMobileChat?: () => void;
  // End session handler
  onEndSession?: () => void;
  // Mute control
  isMicMuted?: boolean;
  onToggleMute?: () => void;
  // Fallback visual states
  isVoiceListening?: boolean;
  isVoiceSupported?: boolean;
  isProcessing?: boolean;
}

export const AvatarControls: React.FC<AvatarControlsProps> = ({
  showChatToggle = false,
  isDesktopChatVisible = false,
  onToggleDesktopChat,
  isMobileChatVisible = false,
  onToggleMobileChat,
  onEndSession,
  isMicMuted = false,
  onToggleMute,
  isVoiceListening = false,
  isVoiceSupported = true,
  isProcessing = false,
}) => {
  const { t } = useTranslation();
  const { interrupt } = useInterrupt();
  const { isAvatarTalking } = useStreamingAvatarContext();

  // Determine if we're on mobile
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Determine which chat to toggle and current state
  const handleChatToggle = isMobile ? onToggleMobileChat : onToggleDesktopChat;
  const isChatVisible = isMobile ? isMobileChatVisible : isDesktopChatVisible;

  return (
    <>
      {/* Waveform overlay removed for VAD experience */}

      <div className="avatar-controls">
        {/* Mute button - controls continuous listening */}
        {onToggleMute && (
          <button
            type="button"
            className={`avatar-control-btn mute-btn ${isMicMuted ? 'muted' : 'active'}`}
            onClick={onToggleMute}
            disabled={!isVoiceSupported}
            data-tooltip={
              !isVoiceSupported
                ? t('avatar.voiceNotSupported', 'Voice not supported')
                : isMicMuted
                  ? t('avatar.unmute', 'Unmute microphone')
                  : t('avatar.mute', 'Mute microphone')
            }
          >
            {isMicMuted ? (
              <svg
                className="voice-btn-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            ) : (
              <svg
                className="voice-btn-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            )}
            {/* Wave animations for heartbeat/ripple effect when listening and not processing */}
            {!isMicMuted && isVoiceListening && !isProcessing && (
              <>
                <span className="voice-btn-wave" />
                <span className="voice-btn-wave voice-btn-wave-2" />
              </>
            )}
          </button>
        )}

        {/* Interrupt button - stop avatar speaking */}
        {isAvatarTalking && (
          <button
            type="button"
            className="avatar-control-btn interrupt-btn"
            onClick={interrupt}
            data-tooltip={t('avatar.interrupt', 'Interrupt')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          </button>
        )}

        {/* Chat toggle button */}
        {showChatToggle && handleChatToggle && (
          <button
            type="button"
            className={`avatar-control-btn chat-toggle-btn ${isChatVisible ? 'active' : ''}`}
            onClick={handleChatToggle}
            data-tooltip={
              isChatVisible ? t('avatar.hideChat', 'Hide chat') : t('avatar.showChat', 'Show chat')
            }
          >
            {isChatVisible ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            )}
          </button>
        )}

        {/* End Session button — power button icon */}
        {onEndSession && (
          <button
            type="button"
            className="avatar-control-btn end-session-btn"
            onClick={() => onEndSession()}
            data-tooltip={t('avatar.endSession', 'End session')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
              <line x1="12" y1="2" x2="12" y2="12" />
            </svg>
          </button>
        )}
      </div>
    </>
  );
};
